import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import { AuthActions } from '@components/auth/auth.actions';
import { Session } from '@providers/session/session.service';
import { reportAncestryActions } from './ancestry.actions';
import { ReportAncestryService } from './ancestry.service';
import { ReportAncestryState } from './ancestry.state.interface';

/**
 * Report Ancestry Effects.
 */
@Injectable()
export class ReportAncestryEffects extends ReportAncestryService {
  private readonly actions$ = inject(Actions);
  private readonly httpClient = inject(HttpClient);
  private readonly session = inject(Session);

  readonly fetchContinentCoordinates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reportAncestryActions.fetchContinentCoordinates),
      switchMap(() => {
        if (!this.session.isAuthenticated()) {
          return of(
            reportAncestryActions.fetchContinentCoordinatesSuccess({
              data: undefined
            })
          );
        }
        return this.httpClient
          .get<
            ReportAncestryState['continentCoordinatesQuery']['data']
          >('/assets/json/ancestry/continent-coordinates.json')
          .pipe(
            map((data) =>
              reportAncestryActions.fetchContinentCoordinatesSuccess({
                data
              })
            ),
            catchError((error: Error) =>
              of(
                reportAncestryActions.fetchContinentCoordinatesFailed({
                  error:
                    error.message || ('An unknown error occurred' as string)
                })
              )
            )
          );
      })
    )
  );

  readonly fetchContinentStyles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reportAncestryActions.fetchContinentStyles),
      switchMap(() => {
        if (this.session.isAuthenticated()) {
          return this.httpClient
            .get<
              ReportAncestryState['continentStylesQuery']['data']
            >('/assets/json/ancestry/continent-styles.json')
            .pipe(
              map((response) =>
                reportAncestryActions.fetchContinentStylesSuccess({
                  data: response
                })
              ),
              catchError((error: Error) => {
                return of(
                  reportAncestryActions.fetchContinentStylesFailed({
                    error:
                      error.message || ('An unknown error occurred' as string)
                  })
                );
              })
            );
        }
        return of(
          reportAncestryActions.fetchContinentStylesSuccess({ data: [] })
        );
      })
    )
  );

  readonly fetchReportAncient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reportAncestryActions.fetchReportAncient),
      switchMap(() => {
        if (this.session.isAuthenticated()) {
          return this.fetchAncient().pipe(
            map((response) =>
              reportAncestryActions.fetchReportAncientSuccess({
                data: response
              })
            ),
            catchError((error: Error) => {
              return of(
                reportAncestryActions.fetchReportAncientFailed({
                  error:
                    error.message || ('An unknown error occurred' as string)
                })
              );
            })
          );
        }
        return of(
          reportAncestryActions.fetchReportAncientSuccess({ data: undefined })
        );
      })
    )
  );

  readonly fetchReportAncientVisibility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reportAncestryActions.fetchReportAncientVisibility),
      switchMap(() => {
        if (this.session.isAuthenticated()) {
          return this.fetchSettings().pipe(
            map((response) => {
              let status: ReportAncestryState['reportAncientVisibilityQuery']['status'] =
                'hidden';
              if (Array.isArray(response?.results) && response.results.length) {
                const setting = response.results.find(
                  (setting) =>
                    setting.resource === 'ancient-ancestry-visibility'
                );
                if (setting) {
                  if (setting.value === 'enabled') {
                    status = 'visible';
                  } else if (setting.value === 'if-available-then-enabled') {
                    status = 'visible-if-available';
                  }
                }
              }
              return status;
            }),
            catchError(() => of('hidden'))
          );
        }
        return of('hidden');
      }),
      map((status) =>
        reportAncestryActions.fetchReportAncientVisibilitySuccess({
          status: status
            ? (status as ReportAncestryState['reportAncientVisibilityQuery']['status'])
            : 'hidden'
        })
      ),
      catchError((error: Error) => {
        return of(
          reportAncestryActions.fetchReportAncientVisibilityFailed({
            error: error.message || ('An unknown error occurred' as string)
          })
        );
      })
    )
  );

  readonly fetchReportContinentColors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reportAncestryActions.fetchReportContinentColors),
      switchMap(() => {
        if (this.session.isAuthenticated()) {
          return this.httpClient
            .get<
              ReportAncestryState['continentColorsQuery']['data']
            >('/assets/json/ancestry/continents-subcontinents-colors.json')
            .pipe(
              map((response) =>
                reportAncestryActions.fetchReportContinentColorsSuccess({
                  data: response
                })
              ),
              catchError((error: Error) => {
                return of(
                  reportAncestryActions.fetchReportContinentColorsFailed({
                    error:
                      error.message || ('An unknown error occurred' as string)
                  })
                );
              })
            );
        }
        return of(
          reportAncestryActions.fetchReportContinentColorsSuccess({
            data: {
              continents: []
            } as ReportAncestryState['continentColorsQuery']['data']
          })
        );
      })
    )
  );

  readonly fetchReportLineage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reportAncestryActions.fetchReportLineage),
      switchMap(() => {
        if (this.session.isAuthenticated()) {
          return this.fetchLineage().pipe(
            map((response) =>
              reportAncestryActions.fetchReportLineageSuccess({
                data: response
              })
            ),
            catchError((error: Error) => {
              return of(
                reportAncestryActions.fetchReportLineageFailed({
                  error:
                    error.message || ('An unknown error occurred' as string)
                })
              );
            })
          );
        }
        return of(
          reportAncestryActions.fetchReportLineageSuccess({ data: undefined })
        );
      })
    )
  );

  readonly fetchReportModern$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reportAncestryActions.fetchReportModern),
      switchMap(() => {
        if (this.session.isAuthenticated()) {
          return this.fetchAncestryModern().pipe(
            map((response) =>
              reportAncestryActions.fetchReportModernSuccess({ data: response })
            ),
            catchError((error: Error) => {
              return of(
                reportAncestryActions.fetchReportModernFailed({
                  error:
                    error.message || ('An unknown error occurred' as string)
                })
              );
            })
          );
        }
        return of(
          reportAncestryActions.fetchReportModernSuccess({ data: undefined })
        );
      })
    )
  );

  readonly fetchUnM49Region$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reportAncestryActions.fetchUnM49Region),
      switchMap(() => {
        if (!this.session.isAuthenticated()) {
          return of(
            reportAncestryActions.fetchUnM49RegionSuccess({
              data: undefined
            })
          );
        }
        return this.httpClient
          .get<
            ReportAncestryState['unM49RegionQuery']['data']
          >('/assets/json/ancestry/unM49.json')
          .pipe(
            map((data: ReportAncestryState['unM49RegionQuery']['data']) =>
              reportAncestryActions.fetchUnM49RegionSuccess({
                data
              })
            ),
            catchError((error: Error) =>
              of(
                reportAncestryActions.fetchUnM49RegionFailed({
                  error:
                    error.message || ('An unknown error occurred' as string)
                })
              )
            )
          );
      })
    )
  );

  readonly onAuthChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login, AuthActions.logout, AuthActions.switchProfile),
      map(() => reportAncestryActions.resetUserReportData())
    )
  );
}
